import { useEffect, useState } from "react";
import { Users, Eye, TrendingUp, Bot, RefreshCw, Globe, Loader2 } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { toast } from "sonner";

// Widget compteur visiteurs pour l'admin (Nold Go).
// Affiche : cartes (aujourd'hui/7j/30j/total), top pages, top referrers, sparkline 30j.
export default function VisitorsWidget() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/analytics/visitors?days=30");
      setData(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, []);

  if (loading) {
    return (
      <div data-testid="admin-visitors-loading" className="bg-white rounded-2xl border border-[#E2DFD8] p-8 flex items-center gap-2 text-sm text-[#75807A]">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement des visiteurs…
      </div>
    );
  }
  if (!data) return null;

  const cards = [
    { key: "today", label: "Aujourd'hui", val: data.today, color: "#F26419", accent: "#FFF3E6" },
    { key: "week",  label: "7 derniers jours", val: data.week, color: "#1A1A1A", accent: "#F5F2EA" },
    { key: "month", label: "30 derniers jours", val: data.month, color: "#16a34a", accent: "#DCFCE7" },
    { key: "all",   label: "Depuis le début", val: data.all_time, color: "#6b21a8", accent: "#F3E8FF" },
  ];

  const daily = data.daily || [];
  const maxPv = Math.max(1, ...daily.map((d) => d.pv));

  return (
    <section data-testid="admin-visitors-widget" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-head font-black text-xl text-[#1A1A1A] flex items-center gap-2">
            <Users className="w-5 h-5 text-[#F26419]" /> Compteur visiteurs
          </h2>
          <p className="text-xs text-[#75807A]">
            Visiteurs uniques (bots filtrés) · rafraîchi toutes les minutes ·
            &nbsp;{data.bot_visits_30d} visites bots ignorées / 30j
          </p>
        </div>
        <button
          data-testid="admin-visitors-refresh"
          onClick={() => { setRefreshing(true); load(); }}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white border border-[#E2DFD8] hover:border-[#F26419] text-xs font-semibold text-[#1A1A1A] disabled:opacity-60">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Rafraîchir
        </button>
      </div>

      {/* Cartes principales */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.key} data-testid={`admin-visitors-card-${c.key}`}
            className="rounded-2xl bg-white border border-[#E2DFD8] p-4">
            <div className="inline-flex items-center gap-1.5 rounded-full text-[10px] font-bold px-2 py-0.5 mb-2"
              style={{ background: c.accent, color: c.color }}>
              <Eye className="w-3 h-3" /> {c.label.toUpperCase()}
            </div>
            <div className="text-3xl font-black" style={{ color: c.color }}>
              {(c.val.uniques || 0).toLocaleString("fr-BE")}
            </div>
            <div className="text-xs text-[#75807A] mt-1">
              visiteurs uniques
            </div>
            <div className="text-[11px] text-[#75807A] mt-0.5">
              {(c.val.pageviews || 0).toLocaleString("fr-BE")} pages vues
            </div>
          </div>
        ))}
      </div>

      {/* Sparkline 30 jours */}
      {daily.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#E2DFD8] p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm text-[#1A1A1A] flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4 text-[#F26419]" /> 30 derniers jours
            </h3>
            <span className="text-[11px] text-[#75807A]">
              pic : {maxPv} pages vues
            </span>
          </div>
          <div className="flex items-end gap-1 h-24" data-testid="admin-visitors-sparkline">
            {daily.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col items-center justify-end group relative min-w-0"
                title={`${d.day} · ${d.pv} vues · ${d.uniques} uniques`}>
                <div className="w-full rounded-t-sm bg-[#F26419]/60 hover:bg-[#F26419] transition-colors"
                  style={{ height: `${(d.pv / maxPv) * 100}%`, minHeight: 2 }} />
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-[10px] text-[#75807A] font-mono">
            <span>{daily[0]?.day.slice(5)}</span>
            <span>{daily[daily.length - 1]?.day.slice(5)}</span>
          </div>
        </div>
      )}

      {/* Top pages */}
      {data.top_pages?.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#E2DFD8] p-5">
          <h3 className="font-bold text-sm text-[#1A1A1A] mb-3 flex items-center gap-1.5">
            <Globe className="w-4 h-4 text-[#F26419]" /> Pages les plus vues (30j)
          </h3>
          <ul className="divide-y divide-[#F0EBE1]">
            {data.top_pages.slice(0, 10).map((p) => (
              <li key={p.path} data-testid={`admin-visitors-page-${p.path}`}
                className="py-2 flex items-center justify-between gap-3">
                <span className="font-mono text-xs text-[#1A1A1A] truncate">{p.path}</span>
                <div className="flex items-center gap-3 shrink-0 text-xs">
                  <span className="text-[#75807A]">{p.uniques} uniques</span>
                  <span className="rounded-full bg-[#FFF3E6] text-[#7A3A00] font-bold px-2 py-0.5">
                    {p.views}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Top referrers */}
      {data.top_referrers?.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#E2DFD8] p-5">
          <h3 className="font-bold text-sm text-[#1A1A1A] mb-3">🌍 D'où viennent les visiteurs</h3>
          <ul className="divide-y divide-[#F0EBE1]">
            {data.top_referrers.slice(0, 8).map((r, i) => (
              <li key={i} className="py-2 flex items-center justify-between gap-3">
                <a href={r.ref} target="_blank" rel="noreferrer"
                  className="text-xs text-[#F26419] hover:underline truncate">
                  {r.ref}
                </a>
                <span className="rounded-full bg-[#F5F2EA] text-[#1A1A1A] font-bold text-xs px-2 py-0.5">
                  {r.n}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Répartition par domaine */}
      {data.hosts?.length > 1 && (
        <div className="rounded-2xl bg-white border border-[#E2DFD8] p-5">
          <h3 className="font-bold text-sm text-[#1A1A1A] mb-3">Par domaine (30j)</h3>
          <ul className="grid grid-cols-2 gap-2">
            {data.hosts.map((h) => (
              <li key={h.host} className="rounded-lg bg-[#F5F2EA] p-3">
                <div className="font-semibold text-xs text-[#1A1A1A] truncate">{h.host}</div>
                <div className="text-lg font-black text-[#F26419]">{h.n}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
